import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Label, Input, Badge, Card, CardBody, FormGroup } from "reactstrap";
import useProjects from "../../Hooks/useProjects";
import { checkUserConflictsApi } from "../../apiServices/leadAssignment.services";
import { handleUserChangesApi, resumeUserApi } from "../../apiServices/automationRule.services";
import { toast } from "react-toastify";

const CreateRuleModal = ({ isOpen, toggle, onSubmit, editData, onRuleUpdated }) => {
  const { projects } = useProjects({ onlyHavingForms: true });
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [removedUsers, setRemovedUsers] = useState([]);
  const [removedUserConfigs, setRemovedUserConfigs] = useState({});
  const [rulePausedUsers, setRulePausedUsers] = useState([]);
  const [formData, setFormData] = useState({
    ruleName: "", projectIds: [], includeUsers: [], autoAssignUpcomingLeads: false,
    lockPreviousOwners: false, autoCreateTask: false, taskTitle: "Follow Up Lead",
    taskDescription: "", taskPriority: "medium", dueAfterMinutes: 60,
  });

  useEffect(() => {
    if (editData) {
      setFormData({
        ruleName: editData?.ruleName || "",
        projectIds: editData?.projectIds || [],
        includeUsers: editData?.includeUsers || [],
        autoAssignUpcomingLeads: editData?.autoAssignUpcomingLeads || false,
        lockPreviousOwners: editData?.lockPreviousOwners || false,
        autoCreateTask: editData?.autoCreateTask || false,
        taskTitle: editData?.taskTitle || "Follow Up Lead",
        taskDescription: editData?.taskDescription || "",
        taskPriority: editData?.taskPriority || "medium",
        dueAfterMinutes: editData?.dueAfterMinutes || 60,
      });
      setRemovedUsers([]);
      setRemovedUserConfigs({});
      setRulePausedUsers(editData?.pausedUsers || []);
    } else {
      setRulePausedUsers([]);
    }
  }, [editData]);

  useEffect(() => {
    if (editData?.projectIds?.length) {
      handleProjectsChange(editData.projectIds.map((p) => ({ label: p.projectName, value: p._id })));
    } else {
      handleProjectsChange([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  const handleProjectsChange = async (selected) => {
    const projectIds = selected?.map((x) => x.value) || [];
    setFormData((p) => ({ ...p, projectIds }));
    try {
      const res = await checkUserConflictsApi({ projectIds });
      const selectedUsers = editData?.includeUsers || [];
      const fetchedAllowed = res?.data?.allowedUsers || [];
      const fetchedInactive = res?.data?.inactiveUsers || [];

      // Filter allowedUsers to only include active employees
      const activeAllowed = fetchedAllowed.filter((u) => u.isActive !== false && (!u.employeeStatus || u.employeeStatus === "active"));

      setAllowedUsers([
        ...activeAllowed,
        ...selectedUsers.filter((su) => su.isActive !== false && (!su.employeeStatus || su.employeeStatus === "active") && !activeAllowed.some((au) => au._id === su._id)),
      ]);
      setBlockedUsers(res?.data?.blockedUsers || []);
      setInactiveUsers(fetchedInactive);
    } catch (err) {
      console.log(err);
    }
  };

  const formatUserLabel = (u, suffix = "") => {
    if (!u) return "";
    const name = u.firstName ? `${u.firstName} ${u.lastName}`.trim() : (u.name || u.label || u._id || "");
    const des = typeof u.designationId === "object" && u.designationId?.title ? u.designationId.title : (u.designation || "");
    const email = u.email || "";
    const details = [email, des].filter(Boolean).join(" - ");
    const base = details ? `${name} (${details})` : name;
    return suffix ? `${base} ${suffix}` : base;
  };

  const getActiveUserOptions = () => {
    const list = [];
    const seen = new Set();

    (formData.includeUsers || []).forEach((u) => {
      if (u.role === "super_admin") return;
      const id = String(u._id || u.value || u);
      if (!seen.has(id)) {
        seen.add(id);
        const name = formatUserLabel(u);
        list.push({ _id: id, name, email: u.email || "" });
      }
    });

    (allowedUsers || []).forEach((u) => {
      if (u.role === "super_admin") return;
      const id = String(u._id || u.value || u);
      if (!seen.has(id)) {
        seen.add(id);
        const name = formatUserLabel(u);
        list.push({ _id: id, name, email: u.email || "" });
      }
    });

    return list;
  };

  const handleIncludeUsersChange = (selected) => {
    const newIncludeUsers = (selected || []).map((x) => {
      const existing = (formData.includeUsers || []).find((u) => String(u._id || u) === String(x.value)) ||
                       (allowedUsers || []).find((u) => String(u._id) === String(x.value)) ||
                       (blockedUsers || []).find((u) => String(u._id) === String(x.value));
      if (existing) return existing;
      return {
        _id: x.value,
        firstName: x.userObj?.firstName || x.label.split(" ")[0],
        lastName: x.userObj?.lastName || x.label.split(" ")[1] || "",
        email: x.email || "",
        designationId: x.userObj?.designationId,
        designation: x.userObj?.designation,
      };
    });

    if (editData && editData.includeUsers) {
      const newSelectedIds = newIncludeUsers.map((u) => String(u._id));
      const newlyRemoved = (editData.includeUsers || []).filter((ou) => !newSelectedIds.includes(String(ou._id || ou)));
      setRemovedUsers(newlyRemoved);

      const activeOpts = newIncludeUsers.length ? newIncludeUsers : getActiveUserOptions();
      const defaultTargetId = activeOpts.length ? activeOpts[0]._id : "";

      const newConfigs = {};
      newlyRemoved.forEach((u) => {
        const uId = u._id || u;
        const uName = formatUserLabel(u) || uId;
        newConfigs[uId] = removedUserConfigs[uId] || {
          userId: uId, userName: uName, actionType: "pause",
          pauseType: "infinite", durationMinutes: 60, customDate: "", transferType: "all_active_users",
          transferToUser: defaultTargetId, reason: "",
        };
      });
      setRemovedUserConfigs(newConfigs);
    }
    setFormData((p) => ({ ...p, includeUsers: newIncludeUsers }));
  };

  const handleConfigChange = (userId, field, value) => {
    setRemovedUserConfigs((prev) => ({
      ...prev, [userId]: { ...prev[userId], [field]: value },
    }));
  };

  const handleRestoreUser = (userToRestore) => {
    const uId = String(userToRestore._id || userToRestore);

    setFormData((prev) => {
      const exists = (prev.includeUsers || []).some((u) => String(u._id || u) === uId);
      if (exists) return prev;
      return {
        ...prev,
        includeUsers: [...(prev.includeUsers || []), userToRestore],
      };
    });

    setRemovedUsers((prev) => prev.filter((u) => String(u._id || u) !== uId));

    setRemovedUserConfigs((prev) => {
      const copy = { ...prev };
      delete copy[uId];
      return copy;
    });
  };

  const handleUnpauseUser = async (userId, userName) => {
    try {
      if (editData && editData._id) {
        await resumeUserApi(editData._id, { userId });
        toast.success(`${userName} unpaused/restored successfully! Status is now active (🟢 Green).`);
        setRulePausedUsers((prev) => prev.filter((p) => String(p.userId?._id || p.userId) !== String(userId)));
        const uObj = (allowedUsers || []).find((x) => String(x._id) === String(userId)) ||
                     (editData?.includeUsers || []).find((x) => String(x._id) === String(userId)) ||
                     { _id: userId, firstName: userName.split(" ")[0], lastName: userName.split(" ")[1] || "" };

        setFormData((prev) => {
          const exists = (prev.includeUsers || []).some((x) => String(x._id || x) === String(userId));
          if (exists) return prev;
          return { ...prev, includeUsers: [...(prev.includeUsers || []), uObj] };
        });

        if (onRuleUpdated) onRuleUpdated();
      }
    } catch (err) {
      toast.error(err?.message || "Failed to unpause user");
    }
  };

  const handleSubmit = async () => {
    try {
      const currentIncludeIds = new Set((formData.includeUsers || []).map((u) => String(u._id || u)));
      const actualRemovedUsers = (removedUsers || []).filter((u) => !currentIncludeIds.has(String(u._id || u)));

      if (editData && actualRemovedUsers.length > 0) {
        const userChangesPayload = actualRemovedUsers.map((u) => {
          const uId = String(u._id || u);
          const cfg = removedUserConfigs[uId] || {};
          return {
            userId: uId,
            actionType: cfg.actionType || "pause",
            pauseType: cfg.pauseType || "infinite",
            pausedUntil: cfg.actionType === "pause" && cfg.pauseType === "scheduled" && cfg.customDate ? new Date(cfg.customDate).toISOString() : undefined,
            durationMinutes: cfg.actionType === "pause" && cfg.pauseType === "scheduled" && !cfg.customDate ? cfg.durationMinutes : undefined,
            transferType: cfg.transferType || "all_active_users",
            transferToUser: cfg.transferToUser,
            reason: cfg.reason,
          };
        });
        await handleUserChangesApi(editData._id, { userChanges: userChangesPayload });
      }

      await onSubmit({
        ...formData,
        projectIds: formData.projectIds?.map((p) => p._id || p),
        includeUsers: formData.includeUsers?.map((u) => u._id || u),
      });

      if (onRuleUpdated) onRuleUpdated();
      toggle();
    } catch (error) {
      toast.error(error?.message || error?.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>{editData ? "Edit Automation Rule" : "Create Automation Rule"}</ModalHeader>
      <ModalBody>
        <Row className="g-3">
          <Col md={6}>
            <Label> Rule Name </Label>
            <Input placeholder="E.g. Auto Assign Rule 1" value={formData.ruleName} onChange={(e) => setFormData((p) => ({ ...p, ruleName: e.target.value }))} />
          </Col>

          <Col md={6}>
            <Label> Project </Label>
            <Select
              isMulti closeMenuOnSelect={false}
              value={(formData.projectIds || []).map((p) => ({
                label: typeof p === "object" ? p?.projectName : projects.find((x) => x._id === p)?.projectName || "",
                value: typeof p === "object" ? p?._id : p,
              }))}
              options={projects.map((p) => ({ label: p.projectName, value: p._id }))}
              onChange={(selected) => {
                setFormData((prev) => ({ ...prev, projectIds: selected?.map((x) => ({ _id: x.value, projectName: x.label })) || [] }));
                handleProjectsChange(selected);
              }}
            />
          </Col>

          <Col md={12}>
            <Label> Include Users </Label>
            <Select
              isMulti
              value={(formData.includeUsers || []).map((u) => ({
                label: formatUserLabel(u),
                value: u._id || u,
                email: u.email,
                userObj: u,
              }))}
              options={[
                ...allowedUsers.map((u) => ({
                  label: formatUserLabel(u),
                  value: u._id,
                  email: u.email,
                  isDisabled: false,
                  userObj: u,
                })),
                ...blockedUsers.map((u) => ({
                  label: formatUserLabel(u),
                  value: u._id,
                  email: u.email,
                  isDisabled: false,
                  userObj: u,
                })),
              ]}
              onChange={handleIncludeUsersChange}
            />
          </Col>

          {/* Temporarily Paused Users Info & Restore Panel */}
          {editData && rulePausedUsers.length > 0 && (
            <Col md={12}>
              <Card className="border border-warning bg-warning-subtle shadow-sm">
                <CardBody className="p-3">
                  <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                    <i className="mdi mdi-pause-circle-outline text-warning fs-5" />
                    Temporarily Paused Members in this Rule ({rulePausedUsers.length})
                  </h6>
                  <p className="small text-muted mb-2">
                    These members are temporarily paused from receiving new leads. Click Restore below to unpause them.
                  </p>

                  <div className="d-flex flex-column gap-2 mt-2">
                    {rulePausedUsers.map((p) => {
                      const u = p.userId;
                      const uId = typeof u === "object" ? u?._id : p.userId;
                      
                      const foundUser = typeof u === "object" && u?.firstName ? u :
                        (allowedUsers || []).find((x) => String(x._id) === String(uId)) ||
                        (blockedUsers || []).find((x) => String(x._id) === String(uId)) ||
                        (inactiveUsers || []).find((x) => String(x._id) === String(uId)) ||
                        (editData?.includeUsers || []).find((x) => String(x._id || x) === String(uId));

                      const name = foundUser?.firstName ? `${foundUser.firstName} ${foundUser.lastName}`.trim() :
                        (foundUser?.email || String(uId));
                      const email = foundUser?.email || (typeof u === "object" ? u?.email : "");

                      return (
                        <div key={uId} className="bg-white p-2 px-3 rounded border d-flex justify-content-between align-items-center shadow-sm">
                          <div className="d-flex align-items-center gap-2">
                            <i className="mdi mdi-account-clock text-warning fs-5" />
                            <div>
                              <span className="fw-semibold text-dark me-2">🟡 {name}</span>
                              {email && <span className="text-muted small me-2">({email})</span>}
                              <Badge color="warning" pill className="ms-1">Temporarily Paused</Badge>
                              {p.reason && <span className="ms-2 small text-muted">Reason: {p.reason}</span>}
                            </div>
                          </div>
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handleUnpauseUser(uId, name)}
                          >
                            <i className="mdi mdi-play-circle-outline me-1" /> Restore / Unpause User
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}

          {/* Excluded Users Info Panel (On Leave, Resigned, Disabled) */}
          {inactiveUsers.length > 0 && (
            <Col md={12}>
              <Card className="border border-secondary-subtle bg-light shadow-sm">
                <CardBody className="p-3">
                  <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                    <i className="mdi mdi-account-off-outline text-warning fs-5" />
                    Disabled / Resigned / On Leave Users ({inactiveUsers.length})
                  </h6>
                  <p className="small text-muted mb-2">
                    Inactive users skip new leads. Existing leads remain assigned.
                  </p>

                  <div className="d-flex flex-wrap gap-2">
                    {inactiveUsers.map((u) => {
                      const statusLabel = u.employeeStatus === "leave" ? "On Leave" :
                                          u.employeeStatus === "resigned" ? "Resigned" :
                                          u.employeeStatus === "security" ? "Security" :
                                          u.employeeStatus === "terminated" ? "Terminated" : "Disabled";
                      const badgeColor = u.employeeStatus === "leave" ? "warning" :
                                         u.employeeStatus === "resigned" ? "dark" : "danger";
                      return (
                        <div key={u._id} className="badge bg-white text-dark border p-2 d-flex align-items-center gap-2 shadow-sm rounded">
                          <i className="mdi mdi-account-circle-outline text-secondary fs-6" />
                          <span className="fw-semibold">{formatUserLabel(u)}</span>
                          <Badge color={badgeColor} pill className="ms-1">{statusLabel}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}

          {/* Embedded Permanent vs Temporary Action Panel inside Edit Modal */}
          {editData && removedUsers.length > 0 && (
            <Col md={12}>
              <Card className="border border-warning bg-warning-subtle">
                <CardBody className="p-3">
                  <h6 className="fw-bold text-dark mb-2"><i className="mdi mdi-alert-circle text-warning me-1" /> Config Action for Removed User(s) ({removedUsers.length})</h6>
                  <p className="small text-muted mb-3">Choose whether removed users are taking a Temporary Pause (purani leads safe rahengi) or being Permanently Removed (leads transfer hongi).</p>

                  {removedUsers.map((u) => {
                    const uId = u._id || u;
                    const uName = u.firstName ? `${u.firstName} ${u.lastName}` : (u.label || u.email || uId);
                    const cfg = removedUserConfigs[uId] || {};
                    const activeOpts = getActiveUserOptions();

                    return (
                      <div key={uId} className="bg-white p-3 rounded border mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong className="text-dark"><i className="mdi mdi-account-remove me-1 text-danger" /> {uName} {u.email ? `(${u.email})` : ""}</strong>
                          <Button
                            color="success"
                            outline
                            size="sm"
                            className="py-0 px-2 fs-7 fw-semibold"
                            onClick={() => handleRestoreUser(u)}
                            title="Restore user back to rule"
                          >
                            <i className="mdi mdi-undo-variant me-1" /> Restore to Rule
                          </Button>
                        </div>
                        <Row className="g-2">
                          <Col md={6}>
                            <Label className="small fw-semibold mb-1">Action Mode:</Label>
                            <div>
                              <FormGroup check inline className="me-3">
                                <Label check className="small">
                                  <Input type="radio" name={`action_${uId}`} checked={cfg.actionType === "pause"} onChange={() => handleConfigChange(uId, "actionType", "pause")} /> ⏸️ Temporary Pause
                                </Label>
                              </FormGroup>
                              <FormGroup check inline>
                                <Label check className="small text-danger">
                                  <Input type="radio" name={`action_${uId}`} checked={cfg.actionType === "permanent_remove"} onChange={() => handleConfigChange(uId, "actionType", "permanent_remove")} /> 🔴 Permanent Removal
                                </Label>
                              </FormGroup>
                            </div>
                          </Col>

                          <Col md={6}>
                            {cfg.actionType === "pause" ? (
                              <div>
                                <Label className="small fw-semibold mb-1">Pause Duration:</Label>
                                <Row className="g-1">
                                  <Col md={6}>
                                    <Input type="select" bsSize="sm" value={cfg.pauseType} onChange={(e) => handleConfigChange(uId, "pauseType", e.target.value)}>
                                      <option value="infinite">♾️ Until I turn ON</option>
                                      <option value="scheduled">⏱️ Scheduled Time</option>
                                    </Input>
                                  </Col>
                                  {cfg.pauseType === "scheduled" && (
                                    <Col md={6}>
                                      <Input type="select" bsSize="sm" value={cfg.durationMinutes} onChange={(e) => handleConfigChange(uId, "durationMinutes", Number(e.target.value))}>
                                        <option value={60}>1 Hour</option>
                                        <option value={240}>4 Hours</option>
                                        <option value={1440}>1 Day (24h)</option>
                                      </Input>
                                    </Col>
                                  )}
                                </Row>
                              </div>
                            ) : (
                              <div>
                                <Label className="small fw-semibold mb-1 text-danger">Lead Transfer Target:</Label>
                                <Input type="select" bsSize="sm" value={cfg.transferType} onChange={(e) => handleConfigChange(uId, "transferType", e.target.value)}>
                                  <option value="all_active_users">Distribute Equally to All Active Members</option>
                                  <option value="individual_user">Transfer to Specific Member</option>
                                  <option value="unassigned">Make Leads Unassigned</option>
                                </Input>
                                {cfg.transferType === "individual_user" && (
                                  <Input
                                    type="select"
                                    bsSize="sm"
                                    className="mt-1"
                                    value={cfg.transferToUser || (activeOpts[0]?._id || "")}
                                    onChange={(e) => handleConfigChange(uId, "transferToUser", e.target.value)}
                                  >
                                    {activeOpts.map((au) => (
                                      <option key={au._id} value={au._id}>{au.name} {au.email ? `(${au.email})` : ""}</option>
                                    ))}
                                  </Input>
                                )}
                              </div>
                            )}
                          </Col>
                        </Row>
                      </div>
                    );
                  })}
                </CardBody>
              </Card>
            </Col>
          )}

          <Col xs={12}>
            <div className="d-flex flex-wrap align-items-center gap-4">
              <div className="d-flex align-items-center">
                <input id="upcomingLeads" type="checkbox" switch="primary" checked={formData.autoAssignUpcomingLeads} onChange={(e) => setFormData((p) => ({ ...p, autoAssignUpcomingLeads: e.target.checked }))} />
                <label htmlFor="upcomingLeads" data-on-label="" data-off-label="" className="mb-0" />
                <span className="ms-2 fw-semibold mb-1">Upcoming Leads</span>
              </div>
              <div className="d-flex align-items-center">
                <input id="lockOwners" type="checkbox" switch="primary" checked={formData.lockPreviousOwners} onChange={(e) => setFormData((p) => ({ ...p, lockPreviousOwners: e.target.checked }))} />
                <label htmlFor="lockOwners" data-on-label="" data-off-label="" className="mb-0" />
                <span className="ms-2 fw-semibold">Lock Owners</span>
              </div>
              <div className="d-flex align-items-center">
                <input id="autoTasks" type="checkbox" switch="primary" checked={formData.autoCreateTask} onChange={(e) => setFormData((p) => ({ ...p, autoCreateTask: e.target.checked }))} />
                <label htmlFor="autoTasks" data-on-label="" data-off-label="" className="mb-0" />
                <span className="ms-2 fw-semibold">Auto Tasks</span>
              </div>
            </div>
          </Col>

          <Col md={6}>
            <Label> Task Title </Label>
            <Input value={formData.taskTitle} onChange={(e) => setFormData((p) => ({ ...p, taskTitle: e.target.value }))} />
          </Col>
          <Col md={6}>
            <Label> Task Priority </Label>
            <Input type="select" value={formData.taskPriority} onChange={(e) => setFormData((p) => ({ ...p, taskPriority: e.target.value }))}>
              <option value="low"> Low </option>
              <option value="medium"> Medium </option>
              <option value="high"> High </option>
            </Input>
          </Col>
          <Col md={12}>
            <Label> Task Description </Label>
            <Input type="textarea" rows={3} value={formData.taskDescription} onChange={(e) => setFormData((p) => ({ ...p, taskDescription: e.target.value }))} />
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle}>Close</Button>
        <Button color="primary" onClick={handleSubmit}>{editData ? "Update Rule" : "Save Rule"}</Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateRuleModal;
